/**
 * Generic page boot loader UI.
 *
 * While the boot chain runs, the stage shows the SAME indicator every other
 * wait in the app shows — the shared `LoadingWave` (equalizer + label +
 * caption) that the mobile lists draw over a refetch — so a page boot and a
 * filter apply no longer look like two different products. The label is
 * the page title ("Loading your page"), the caption the running step
 * ("Loading permissions…"), and the optional "Cancel and sign out" action
 * sits beneath (the `onLogout` prop).
 *
 * No percentage and no bar: `LoadingWave` documents why — a boot is a
 * handful of sequential requests, and a figure derived from their COUNT
 * (the old `(done + 1) / (steps + 2)`) measured nothing the user could act
 * on. The step count still drives the ERROR card, which keeps the card
 * chrome because it carries a message and a Retry action.
 *
 * Behavioural contract is unchanged from the timeline version:
 * dependencies run sequentially via `useDependencyLoader`, `children`
 * only render after every step succeeds (or is skipped), a failed step
 * surfaces a Retry button (`renderError` overrides the default block).
 *
 * Usage:
 *
 *   <PageDependencyLoader dependencies={deps}>
 *     <MyPage />
 *   </PageDependencyLoader>
 */

import React, { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Dependency, DependencyStepState } from '../../dependencies/types';
import {
  UseDependencyLoaderOptions,
  useDependencyLoader,
} from '../../hooks/useDependencyLoader';
import { Button } from '../ui/Button/Button';
import { LoadingWave } from './LoadingWave/LoadingWave';

export interface PageDependencyLoaderProps extends UseDependencyLoaderOptions {
  dependencies: ReadonlyArray<Dependency>;
  children?: ReactNode;
  /** Title shown in the card. */
  title?: string;
  /** When provided, renders the "Cancel and sign out" footer action. */
  onLogout?: () => void;
  /**
   * Optional override for the user-facing error block. Defaults to a generic
   * message + Retry button. Receives the failed step state so callers can
   * surface step-specific copy.
   */
  renderError?: (
    failed: { stepTitle: string; message: string },
    retry: () => void,
  ) => ReactNode;
}

/** Key of the default card title; the English default lives beside it. */
const DEFAULT_TITLE_KEY = 'pages.pageDependencyLoader.title';
const DEFAULT_TITLE = 'Loading your page';

// ── Stage + card ─────────────────────────────────────────────────────
// The stage fills its host content area (the layout's scrollable
// <main>) — NOT the whole viewport — so the surface covers the area
// below the header/breadcrumb without overflowing it. `min-h-full`
// (100% of the parent) instead of `min-h-screen` (100vh) is what keeps
// the stray vertical scrollbar away: AppLayout's <main> is
// `flex flex-col` and the page section is `flex-1 min-h-0`, so the
// ancestor chain resolves the height.
//
// Below 520px the padding goes so the card runs edge to edge — but the card
// keeps `items-center`. It briefly had `max-[520px]:items-stretch`, and on
// a flex ROW the cross axis is vertical: the card was stretched to the full
// height of the content area with its title, bar and hint pinned to the
// top and the rest empty — a screen-tall box (QA 2026-10-22, mobile Orders
// boot). The card's own `max-w-full` already gives it the full width.
const STAGE_CLASS =
  'flex items-center justify-center w-full min-h-full p-8 '
  + 'bg-[var(--color-bg-secondary)] max-[520px]:p-0';

const CARD_CLASS =
  'w-[460px] max-w-full bg-[var(--color-header-bg)] '
  + 'border border-solid border-[var(--color-border)] rounded-xl '
  + 'shadow-[var(--shadow-md)] px-11 pt-10 pb-[30px] animate-ld-rise '
  + 'max-[520px]:px-[22px] max-[520px]:pt-[30px] max-[520px]:pb-[22px]';

// ── Head (title + caption) ───────────────────────────────────────────
const HEAD_CLASS = 'text-center';

const TITLE_CLASS =
  'text-[19px] font-hk-bold text-[var(--color-text-primary)] tracking-[-0.01em] m-0';

const CAPTION_CLASS =
  'mt-[7px] text-[13px] text-[var(--color-text-muted)] '
  + 'inline-flex items-center gap-2';

const PCT_CLASS =
  'font-hk-semibold text-[var(--color-primary)] tabular-nums';

const PCT_SEP_CLASS = 'text-[var(--color-border-strong)]';

// ── Linear progress bar ──────────────────────────────────────────────
const BAR_CLASS =
  'relative h-1.5 rounded-[3px] bg-[var(--color-bg-tertiary)] '
  + 'overflow-hidden mt-[22px] mx-0.5 mb-1.5';

const BAR_FILL_CLASS =
  'absolute left-0 top-0 bottom-0 rounded-[3px] '
  + 'bg-[var(--color-primary)] transition-[width] duration-500 ease-out';

const BAR_FILL_ERROR_CLASS =
  'absolute left-0 top-0 bottom-0 rounded-[3px] '
  + 'bg-[var(--color-danger)] transition-[width] duration-500 ease-out';

const BAR_SHEEN_CLASS =
  'absolute top-0 bottom-0 w-2/5 animate-ld-sheen '
  + 'bg-gradient-to-r from-transparent via-[rgba(255,255,255,0.35)] to-transparent';

// ── Loading view (wave + label + caption + optional sign-out) ────────
const WAVE_BLOCK_CLASS = 'flex flex-col items-center';

// ── Footer ───────────────────────────────────────────────────────────
const FOOT_CLASS =
  'mt-6 pt-[18px] border-t border-solid border-[var(--color-divider)] '
  + 'flex items-center justify-between gap-3';

const FOOT_HINT_CLASS = 'text-[12px] text-[var(--color-text-muted)]';

const CANCEL_BTN_CLASS =
  'border-0 bg-transparent text-[var(--color-text-secondary)] '
  + 'text-[12.5px] font-hk-semibold cursor-pointer px-2 py-1.5 rounded-md '
  + 'hover:text-[var(--color-danger)] hover:bg-[var(--color-bg-hover)] '
  + 'transition-colors duration-fast';

// ── Error block ──────────────────────────────────────────────────────
const ERROR_MESSAGE_CLASS =
  'mt-3 text-center text-[var(--color-danger)] text-[13px]';

const ERROR_ACTIONS_CLASS = 'mt-3 flex justify-center gap-2';

/**
 * Percent + caption derived from the step list, mirroring the design
 * prototype's math: denominator = steps + the "Starting"/"Rendering"
 * bookends; "Starting" counts as complete the moment the loader mounts.
 */
function deriveProgress(
  steps: ReadonlyArray<DependencyStepState>,
  t: TFunction,
): {
  pct: number;
  caption: string;
} {
  const total = steps.length + 2;
  const done = steps.filter(
    (step) => step.status === 'success' || step.status === 'skipped',
  ).length;
  const pct = Math.min(100, Math.round(((done + 1) / total) * 100));

  const running = steps.find((step) => step.status === 'running');
  const failed = steps.find((step) => step.status === 'error');
  let caption: string;
  if (failed) {
    caption = t('pages.pageDependencyLoader.stepFailed', '{{title}} failed', { title: failed.title });
  } else if (running) {
    // The title is the whole caption — NOT prefixed with "Loading". Every entry
    // in `DEPENDENCY_TITLE` is already a present participle, and ten of the
    // thirteen begin with the word "Loading", so prefixing produced
    // "Loading Loading vendors…" on almost every step. The three that do not
    // ("Validating master data", "Fetching master data", "Updating master
    // data") read correctly on their own too.
    caption = t('pages.pageDependencyLoader.loadingStep', '{{title}}…', { title: running.title });
  } else if (done >= steps.length && steps.length > 0) {
    caption = t('pages.pageDependencyLoader.rendering', 'Rendering the page…');
  } else {
    caption = startingUpCaption(t);
  }
  return { pct, caption };
}

/** The caption before any step has run — shared with the static preview. */
function startingUpCaption(t: TFunction): string {
  return t('pages.pageDependencyLoader.startingUp', 'Starting up…');
}

interface LoaderCardProps {
  title: string;
  pct: number;
  caption: string;
  /** Keeps the sheen sweeping — off for the error state. */
  loading: boolean;
  /** Paints the bar fill red. */
  failed?: boolean;
  hint?: string;
  onLogout?: () => void;
  /**
   * ARIA role applied to the STAGE element (the error state passes
   * "alert"). It must live on the stage, not a wrapper: an outer div
   * would break the stage's `min-h-full` chain and the card would jump
   * from the vertical center to the top when the state flips.
   */
  role?: string;
  children?: ReactNode;
}

const LoaderCard: React.FC<LoaderCardProps> = ({
  title,
  pct,
  caption,
  loading,
  failed = false,
  hint,
  onLogout,
  role,
  children,
}) => {
  const { t } = useTranslation();
  const footHint = hint ?? t('pages.pageDependencyLoader.hint', 'This can take a few moments');
  return (
  <div
    className={STAGE_CLASS}
    data-testid="loader-stage"
    role={role}
    aria-busy={loading || undefined}
    aria-live="polite"
  >
    <div className={CARD_CLASS}>
      <div className={HEAD_CLASS}>
        <h2 className={TITLE_CLASS}>{title}</h2>
        <div className={CAPTION_CLASS}>
          <span className={PCT_CLASS}>{pct}%</span>
          <span className={PCT_SEP_CLASS} aria-hidden="true">
            ·
          </span>
          <span>{caption}</span>
        </div>
      </div>

      <div className={BAR_CLASS}>
        {/* Visually hidden native <progress> carries the progressbar
            semantics for assistive tech; the styled divs stay purely
            visual. */}
        <progress className="sr-only" value={pct} max={100} />
        <div
          className={failed ? BAR_FILL_ERROR_CLASS : BAR_FILL_CLASS}
          style={{ width: `${pct}%` }}
        />
        {loading ? <div className={BAR_SHEEN_CLASS} aria-hidden="true" /> : null}
      </div>

      {children}

      <div className={FOOT_CLASS}>
        <span className={FOOT_HINT_CLASS}>{footHint}</span>
        {onLogout ? (
          <button type="button" className={CANCEL_BTN_CLASS} onClick={onLogout}>
            {t('pages.pageDependencyLoader.cancelAndSignOut', 'Cancel and sign out')}
          </button>
        ) : null}
      </div>
    </div>
  </div>
  );
};

interface LoadingViewProps {
  /** The page title — the wave's label. */
  title: string;
  /** The running step — the wave's caption. */
  caption: string;
  onLogout?: () => void;
}

/**
 * The loading state: the shared `LoadingWave` on the stage.
 *
 * `delayMs={0}`: the wave's grace period exists so a CACHED answer (a sort
 * on a warm grid) never flashes it. A page boot is never that — it always
 * runs at least the version check against the server — and the preview
 * below is the Suspense fallback for a lazy route, which is on screen for
 * exactly as long as the chunk takes. Both deserve an immediate indicator.
 */
const LoadingView: React.FC<LoadingViewProps> = ({ title, caption, onLogout }) => {
  const { t } = useTranslation();
  return (
    <div
      className={STAGE_CLASS}
      data-testid="loader-stage"
      aria-busy
      aria-live="polite"
    >
      <div className={WAVE_BLOCK_CLASS}>
        <LoadingWave label={title} caption={caption} delayMs={0} testId="loader-wave" />
        {onLogout ? (
          <button type="button" className={CANCEL_BTN_CLASS} onClick={onLogout}>
            {t('pages.pageDependencyLoader.cancelAndSignOut', 'Cancel and sign out')}
          </button>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Static preview of the loader. Renders the same stage + wave as the live
 * `<PageDependencyLoader>`, with the "Starting up…" caption — nothing is
 * executed.
 *
 * Intended for the early-boot phase BEFORE the live loader can mount
 * (e.g. while route permissions are still being fetched, or as a lazy
 * route's Suspense fallback). The live loader takes over with identical
 * chrome, so there's no visual jump.
 */
export interface PageDependencyLoaderPreviewProps {
  /** Title shown as the wave's label. Defaults to the same copy the live
   *  loader uses. (`stepTitles` is gone: the wave replaced the percent the
   *  step count used to feed, so nothing read it.) */
  title?: string;
}

export const PageDependencyLoaderPreview: React.FC<
  PageDependencyLoaderPreviewProps
> = ({ title }) => {
  const { t } = useTranslation();
  return (
    <LoadingView
      title={title ?? t(DEFAULT_TITLE_KEY, DEFAULT_TITLE)}
      caption={startingUpCaption(t)}
    />
  );
};

export const PageDependencyLoader: React.FC<PageDependencyLoaderProps> = ({
  dependencies,
  children,
  title: titleProp,
  onLogout,
  renderError,
  ...loaderOptions
}) => {
  const { t } = useTranslation();
  const title = titleProp ?? t(DEFAULT_TITLE_KEY, DEFAULT_TITLE);
  const { status, steps, error, retry } = useDependencyLoader(
    dependencies,
    loaderOptions,
  );

  if (status === 'complete') {
    return <>{children}</>;
  }

  const { pct, caption } = deriveProgress(steps, t);

  if (status === 'error' && error) {
    return (
      <LoaderCard
        role="alert"
        title={title}
        pct={pct}
        caption={caption}
        loading={false}
        failed
        hint={t('pages.pageDependencyLoader.errorHint', 'Something went wrong')}
        onLogout={onLogout}
      >
        {renderError ? (
          renderError(
            { stepTitle: error.stepTitle, message: error.message },
            retry,
          )
        ) : (
          <>
            <p className={ERROR_MESSAGE_CLASS}>
              {error.stepTitle} failed: {error.message}
            </p>
            <div className={ERROR_ACTIONS_CLASS}>
              <Button variant="primary" size="sm" onClick={retry}>
                Retry
              </Button>
            </div>
          </>
        )}
      </LoaderCard>
    );
  }

  return <LoadingView title={title} caption={caption} onLogout={onLogout} />;
};
